
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X } from 'lucide-react';
import { useProducts } from '../context/ProductContext';
import { useState, useEffect, useRef } from 'react';
import { useCart } from '../context/CartContext';

const Navbar = () => {

  const { cartCount } = useCart();

  const navigate = useNavigate();

  const location = useLocation();

  const searchRef = useRef(null);

  const { products } = useProducts();

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const closeMenu = () => setMobileMenuOpen(false);


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsDropdownVisible(false);
    setSearchQuery('');
    closeMenu();
  }, [location.pathname]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim()) {
      const q = query.toLowerCase();
      const results = products.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.fullDescription.toLowerCase().includes(q)
      );
      setSearchResults(results);
      setIsDropdownVisible(true);
    } else {
      setSearchResults([]);
      setIsDropdownVisible(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim() && searchResults.length > 0) {
      navigate(`/product/${searchResults[0].id}`);
      setIsDropdownVisible(false);
    } else if (searchQuery.trim()) {
      navigate('/');
      setIsDropdownVisible(false);
    }
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${mobileMenuOpen ? 'mobile-menu-active' : ''}`}>
      <div className="navbar-content">

        <div className="navbar-left">
          {/* Mobile Hamburger */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo - Left */}
          <Link to="/" onClick={closeMenu} className="navbar-logo">
            HeartyCrafts
          </Link>
        </div>

        {/* Desktop Links - Center */}
        <div className="nav-links">
          <Link to="/" onClick={closeMenu} className="nav-link-item">
            Home
          </Link>
          <Link to="/orders" onClick={closeMenu} className="nav-link-item">
            My Orders
          </Link>

          <div className="mobile-cart-wrapper">
            <Link to="/cart" onClick={closeMenu} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.75rem' }}>
              Cart {cartCount > 0 && cartCount} <ShoppingCart size={12} />
            </Link>
          </div>
        </div>

        <div className="navbar-right">
          {/* Search Bar */}
          <form className="search-container" onSubmit={handleSearchSubmit} ref={searchRef}>
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchQuery.trim()) setIsDropdownVisible(true); }}
            />
            <button type="submit" className="search-btn">
              <Search size={16} />
            </button>

            {/* Search Autocomplete Dropdown */}
            {isDropdownVisible && searchQuery.trim() && (
              <div className="search-dropdown" style={{ color: '#000' }}>
                {searchResults.length > 0 ? (
                  searchResults.map(prod => (
                    <Link
                      key={prod.id}
                      to={`/product/${prod.id}`}
                      className="search-result-item"
                      onClick={() => setIsDropdownVisible(false)}
                    >
                      <img src={prod.image} alt={prod.name} className="search-result-image" />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '600', fontSize: '12px' }}>{prod.name}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{prod.category} • ₹{prod.price}</span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div style={{ padding: '1rem', color: 'var(--text-muted)', textAlign: 'center', fontSize: '12px' }}>
                    No results found for "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </form>

          {/* Cart Button mimicking "Connect Wallet" solid pill button */}
          <Link to="/cart" onClick={closeMenu} className="btn btn-primary desktop-cart-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
            Cart {cartCount > 0 && cartCount} <ShoppingCart size={14} />
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
